import newsletterConfig from '../../../newsletter-config.json'
import { NewsletterSectionRow, TableWrapper } from './helpersComponents'
import NewsletterContentText from './NewsletterContentText'
import { bold, headlineLargeBold, headlineMediumBold, textButtonTiny, textNormal, textLarge } from './helpersTypo'
import getPerformanceIntervall from '../../../functions/getPerformanceIntervall'
import getPerformanceUrl from '../../../functions/getPerformanceUrl'
import getShowtime from '../../../functions/getShowtime'
import { nanoid } from "nanoid";
import { truncateText } from '../../../functions/truncateText'
import moment from 'moment';
import separateNewsletterAttributes from '../functions/separateNewsletterAttributes'
import getHiddenAttributes from '../functions/getHiddenAttributes'
import getAttributeIconData from '../functions/getAttributeIconData'
import buildPeriodBands from '../functions/buildPeriodBands'
// languageAttributes kommt aus der Front-Config — EINE Quelle für beide Apps,
// sonst driften die Listen (wie zuvor bei attributesWithIcons passiert)
import frontConfig from '../../../../../front/js/config.json'
import PerformanceTimeEntry from './PerformanceTimeEntry'

// export: Snapshot-Tests rendern die Chips mit den ECHTEN Styles
export const createStyles = ({ textColor, borderColor, fontFamily, buttonBackground, sectionBackground, posterRadius, attributeBackground }) => ({
  cinemaPerformanceSchedule: {
    backgroundColor: sectionBackground,
    borderCollapse: "collapse",
    textAlign: "center",
    borderBottom: `1px solid ${borderColor}`,
    fontFamily: `${fontFamily}, sans-serif`,
    fontSize: "0.8rem",
    marginTop: "1rem",
  },
  performanceTimeCell: {
    borderLeft: `1px solid ${borderColor}`,
    // 10px Luft unter jedem Band: trennt die Attribute optisch von der
    // NÄCHSTEN Vorstellung — Zuordnung bleibt eindeutig
    padding: "0 0px 10px",
    // Band-Zeilen: Pille sitzt oben in der Zeile (uniformer Offset in
    // PerformanceTimeEntry), Attribut-Text wächst nach unten — Uhrzeiten
    // bleiben über alle Spalten auf einer Höhe
    verticalAlign: "top",
  },
  time: {
    ...textButtonTiny(fontFamily),
    display: "inline-block",
    background: buttonBackground,
    padding: "0rem 0.8rem",
    fontWeight: "bold",
    // unten 6px (wie Design-Mockup): die eigenen Attribute stehen dicht an der Pille, gehören
    // sichtbar zu DIESER Vorstellung
    margin: "0.5rem 0 6px"
  },
  timef: {
    display: "inline-block",
    color: "transparent",
    padding: "0rem 0.8rem",
    fontWeight: "bold",
    margin: "0.5rem 0 6px"
  },
  performanceDayHeader: {
    ...bold(fontFamily),
    fontSize: "18px",
    lineHeight: "18px",
    borderLeft: `1px solid ${borderColor}`,
    padding: "10px 0",
    color: textColor
  },
  headerDate: {
    fontSize: "13px",
    ...bold(fontFamily),
  },
  link: {
    color: "white",
  },
  attributeInline: {
    fontSize: "10px",
    // Vertikale Luft als ECHTES symmetrisches Padding, nicht über line-height:
    // Apple Mail/Thunderbird ziehen die line-height-Box sonst zusammen (Text
    // klebt am Rand) bzw. verteilen das Leading ungleich (unten weniger Luft).
    // line-height = Fontgröße macht die Zeile deterministisch; 3px Padding
    // rundum = 16px Chip wie im Mockup, Icon-Grafiken (10px) sitzen exakt
    // mittig. Outlook (Word) ignoriert Span-Padding und rendert kompakt.
    lineHeight: "10px",
    padding: "3px 3px",
    margin: "0 4px 0 0",
    background: attributeBackground,
    display: "inline-block",
    verticalAlign: "middle",
  },
  attributeText: {
    display: "block",
    textAlign: "left",
    fontSize: "11px",
    lineHeight: "14px",
    fontWeight: "bold",
    color: textColor,
    margin: "0",
  },
  poster: {
    display: "block",
    width: "100% !important",
    width: "100%",
    maxWidth: "100%",
    height: "auto",
    marginBottom: "15px",
    borderRadius: posterRadius,
  },
  cinemaTitle: {
    ...headlineMediumBold(fontFamily),
    color: textColor,
    padding: "1rem 1rem 1rem 1.6rem",
  },
  movieTitle: {
    ...headlineMediumBold(fontFamily),
    color: textColor,
    marginBottom: "1rem"
  },
  movieDescription: {
    ...textLarge(fontFamily),
    color: textColor,
    marginBottom: "15px",
  },
  moreButton: {
    ...textLarge(fontFamily),
    textDecoration: "none",
    color: buttonBackground,
  }
});

const buildScheduledPerformances = (performancesByDay, timeSlots, locale, timezone) => {
  if (!performancesByDay || Object.keys(performancesByDay).length === 0) return {};

  const scheduled = {};

  for (const [periodIndex, { start, end }] of timeSlots.entries()) {
    const [sH, sM] = start.split(":").map(Number);
    const [eH, eM] = end.split(":").map(Number);

    for (const [dateKey, perfs] of Object.entries(performancesByDay)) {
      const date = moment(dateKey);
      const startMoment = date.clone().set({ hour: sH, minute: sM });
      const endMoment = date.clone().set({ hour: eH, minute: eM });

      const inPeriod = perfs.filter(({ timeUtc }) => {
        const showtime = getShowtime(timeUtc, locale, timezone)
        if (!showtime) return false;
        const [h, m] = showtime.split(":").map(Number);
        const perfMoment = date.clone().set({ hour: h, minute: m });
        return perfMoment.isBetween(startMoment, endMoment, null, "[]");
      });

      (scheduled[periodIndex] ??= {})[dateKey] = inPeriod;
    }
  }

  return scheduled;
};

function getWeekDayLabels(performancesByDay, currentLang, timezone) {
  const weekDays = moment().localeData(currentLang).weekdays(true);
  return Object.entries(performancesByDay).map(([dateKey]) => {
    const date = moment.utc(dateKey).tz(timezone)
    return [date.date(), weekDays[date.weekday()].substring(0, 2).toUpperCase()];
  });
}

const groupPerformancesByCinema = (performances) => {
  return performances.reduce((acc, perf) => {
    if (!acc[perf.cinemaId]) acc[perf.cinemaId] = [];
    acc[perf.cinemaId].push(perf);
    return acc;
  }, {});
};

const NewsletterMovieList = ({
  itemIds,
  movieData,
  routeMovies,
  allPerformances,
  cinemasData,
  startDate,
  endDate,
  week,
  styles,
  translation,
  hiddenAttributes,
  currentCinemaUrl
}) => {
  return itemIds.map((moviePk, index) => {
    const movie = movieData[moviePk];
    if (!movie) return null;

    const routeMovie = routeMovies[moviePk];
    if (!routeMovie)
      console.error("No route for movie", movie, routeMovies)

    const performancePks = movie.performances || [];
    const performances = performancePks.map((pk) => allPerformances[pk]).filter(Boolean);
    if (performances.length === 0) return null;
    const posterUrl = movie?.images?.poster?.url
    const performancesByCinema = groupPerformancesByCinema(performances);
    if (_.isEmpty(performancesByCinema)) return null;

    const paddingBottom = index === itemIds.length - 1 ? 0 : newsletterConfig.movieSectionMovieSpacing;
    const truncated = truncateText(movie.description, 480);

    return (
      <tr key={moviePk}>
        <td align="center" style={{ paddingBottom: paddingBottom }}>
          <NewsletterMovieBlock
            performancesByCinema={performancesByCinema}
            cinemasData={cinemasData}
            startDate={startDate}
            endDate={endDate}
            week={week}
            styles={styles}
            routeMovie={routeMovie}
            translation={translation}
            posterUrl={posterUrl}
            title={movie.titleDisplay ? movie.titleDisplay : movie.title}
            description={truncated}
            hiddenAttributes={hiddenAttributes}
            currentCinemaUrl={currentCinemaUrl}
          />
        </td>
      </tr>
    );
  });
};

const MoviePosterWithDescription = ({ posterUrl, title, description, styles, routeMovie, currentLang }) => {
  const movieUrl = routeMovie?.uri_list?.[currentLang];

  const LinkWrapper = movieUrl
    ? ({ children }) => (
      <a href={movieUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
        {children}
      </a>
    )
    : ({ children }) => <>{children}</>;

  return (
    <>
      <TableWrapper width="47%" align="left" className="flexibleContainer">
        <tr>
          <td align="left">
            <LinkWrapper>
              <img
                className="flexibleImage"
                src={posterUrl}
                alt={`Poster von ${title}`}
                style={{
                  ...styles.poster,
                }}
              />
            </LinkWrapper>
          </td >
        </tr>
      </TableWrapper>

      <TableWrapper width="47%" align="right" className="flexibleContainer">
        <tr>
          <td align="left">
            <LinkWrapper>
              <h2 style={styles.movieTitle}>{title}</h2>
              <p style={styles.movieDescription}>{description}</p>
            </LinkWrapper>
          </td>
        </tr>
      </TableWrapper>
    </>
  );
};

const NewsletterMovieBlock = ({ performancesByCinema, cinemasData, startDate, endDate, week, styles, routeMovie, translation, posterUrl, title, description, hiddenAttributes, currentCinemaUrl }) => {

  const currentLang = translation.currentLang

  return (
    <>
      <MoviePosterWithDescription key={nanoid()} posterUrl={posterUrl} title={title} description={description} styles={styles} routeMovie={routeMovie} currentLang={currentLang} />
      <MoviePerformancesByCinema key={nanoid()} performancesByCinema={performancesByCinema} cinemasData={cinemasData} startDate={startDate} endDate={endDate} week={week} styles={styles} routeMovie={routeMovie} translation={translation} hiddenAttributes={hiddenAttributes} currentCinemaUrl={currentCinemaUrl} />
    </>
  )
}

const MoviePerformancesByCinema = ({ performancesByCinema, cinemasData, startDate, endDate, week, styles, routeMovie, translation, hiddenAttributes, currentCinemaUrl }) => {
  if (_.keys(performancesByCinema).length === 0) {
    return ""
  }
  return Object.entries(performancesByCinema).map(([cinemaId, performances]) => {
    const cinema = cinemasData[cinemaId]
    return (
      <CinemaPerformanceSchedule key={nanoid()} performances={performances} cinema={cinema} startDate={startDate} endDate={endDate} week={week} styles={styles} routeMovie={routeMovie} translation={translation} hiddenAttributes={hiddenAttributes} currentCinemaUrl={currentCinemaUrl} />
    )
  })
}

const CinemaPerformanceSchedule = ({ performances, cinema, startDate, endDate, styles, routeMovie, translation, hiddenAttributes, currentCinemaUrl }) => {

  const tableWidth = newsletterConfig.bodyWith
  const cellWidth = "14.2857%"
  const timezone = translation.timezone
  const locale = translation.locale
  const currentLang = translation.currentLang
  const performancesByDay = getPerformanceIntervall(performances, startDate, endDate);
  if (_.isEmpty(performancesByDay)) return null;

  const weekDayLabels = getWeekDayLabels(performancesByDay, currentLang, timezone);
  const scheduledPerformances = buildScheduledPerformances(performancesByDay, newsletterConfig.perfGridTimeSlots, locale, timezone);

  return (
    <TableWrapper style={styles.cinemaPerformanceSchedule} width={tableWidth}>
      <tr>
        <td>
          {<CinemaRowHeader cinema={cinema} style={styles.cinemaTitle} />}
          <table width={tableWidth} >
            <thead>
              <tr>
                {weekDayLabels.map(([day, label], i) => (
                  <PerformanceDayHeader key={nanoid()} day={label} date={day} styles={styles} hasBorder={i != 0} width={cellWidth} />
                ))}
              </tr>
            </thead>

            <tbody>
              {Object.values(scheduledPerformances).map((performancesInPeriod) =>
                buildPeriodBands(performancesInPeriod).map(band => (
                  <tr key={nanoid()}>
                    {band.map((perf, i) => (
                      <PerformanceTimeCell
                        key={nanoid()}
                        performances={perf ? [perf] : []}
                        routeMovie={routeMovie}
                        styles={styles}
                        translation={translation}
                        hasBorder={i !== 0}
                        width={cellWidth}
                        hiddenAttributes={hiddenAttributes}
                        currentCinemaUrl={currentCinemaUrl}
                      />
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </td>
      </tr>
    </TableWrapper>
  );
};

const CinemaRowHeader = ({ cinema, style }) => {
  return (
    <TableWrapper>
      <tr>
        <NewsletterContentText style={{ ...style }}>{cinema.nameDisplay}</NewsletterContentText>
      </tr>
    </TableWrapper>
  );
}

const PerformanceDayHeader = ({ day, date, styles, hasBorder, width }) => {

  const style = hasBorder ? styles.performanceDayHeader : { ...styles.performanceDayHeader, border: "none", width: width }

  return (
    <th style={style}>
      {day}
      <br />
      <span style={styles.headerDate}>{date}.</span>
    </th>
  );
}

export const PerformanceTimeCell = ({ performances, routeMovie, styles, translation, hasBorder, width, hiddenAttributes, currentCinemaUrl }) => {

  const style = hasBorder ? styles.performanceTimeCell : { ...styles.performanceTimeCell, border: "none" }
  const currentLang = translation.currentLang
  const locale = translation.locale
  const timezone = translation.timezone

  if (!performances || performances.length === 0) {
    return (
      <td style={{ ...style, width: width }}>
        <span style={styles.timef}>{'\u00A0'}</span>
      </td>
    )
  }

  const performanceSpans = performances.map(performance => {
    const { border, below } = separateNewsletterAttributes(
      performance.attributes,
      hiddenAttributes,
      frontConfig.languageAttributes
    )
    const perfLink = getPerformanceUrl(performance.id, routeMovie, currentLang)
    const showtime = getShowtime(performance.timeUtc, locale, timezone)
    // Fehlt Grafik oder Basis-URL, liefert getAttributeIconData iconUrl=null und
    // PerformanceTimeEntry rendert den Namen als Text — nie ein kaputtes Bild
    const withIconData = attr => getAttributeIconData(attr, currentCinemaUrl)
    return (
      <PerformanceTimeEntry
        key={performance.id}
        href={perfLink}
        showtime={showtime}
        borderBadges={border.map(withIconData)}
        textAttributes={below.map(withIconData)}
        styles={styles}
      />
    );
  })
  return <td style={{ ...style, width: width }}>{performanceSpans}</td>
}

const NewsletterSectionMoviesPerformances = ({ id,
  movieData,
  allPerformances,
  cinemaData,
  cinemasData,
  newsletterData,
  startDate,
  endDate,
  structureItemData,
  stylesData,
  routeMovies,
  translation,
  currentCinemaUrl
}) => {

  if (!newsletterData || !newsletterData[id] || 0 === newsletterData[id].length || !translation?.currentLang) {
    return null
  }

  const itemIds = newsletterData[id]

  const chosenFont = stylesData.chosenFont
  const colorText = stylesData.secText
  const styles = createStyles({
    textColor: stylesData.mainText,
    borderColor: stylesData.mainText,
    fontFamily: stylesData.chosenFont,
    buttonBackground: stylesData.buttonBackground,
    sectionBackground: stylesData.sectionBackground,
    posterRadius: stylesData.posterRadius,
    attributeBackground: stylesData.attributeBackground,
    buttonRadius: stylesData.buttonRadius,
    buttonBackground: stylesData.buttonBackground,
    buttonText: stylesData.buttonText
  })

  const hiddenAttributes = getHiddenAttributes()

  return ([
    <NewsletterSectionRow key={nanoid()}>
      <TableWrapper>
        <tr>
          <NewsletterContentText style={{ paddingTop: "102px", paddingBottom: "34px" }}>
            <h3 style={{ ...{ color: colorText, textAlign: 'center' }, ...headlineLargeBold(chosenFont) }}>
              {structureItemData.headlineFront ? structureItemData.headlineFront.toUpperCase() : ''}
            </h3>
          </NewsletterContentText>
        </tr>
      </TableWrapper>
    </NewsletterSectionRow>,

    <NewsletterSectionRow key={nanoid()}>
      <TableWrapper>
        <NewsletterMovieList
          itemIds={itemIds}
          movieData={movieData}
          routeMovies={routeMovies}
          allPerformances={allPerformances}
          cinemasData={cinemasData}
          startDate={startDate}
          endDate={endDate}
          week={cinemaData.week.isoWeek}
          styles={styles}
          translation={translation}
          hiddenAttributes={hiddenAttributes}
          currentCinemaUrl={currentCinemaUrl}
        />
      </TableWrapper>
    </NewsletterSectionRow>
  ])
}

export default NewsletterSectionMoviesPerformances